import { ChevronDown, Download, Loader2, RefreshCw, Search, X } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { FacetCombobox } from "@/components/ui/facet-combobox";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { useI18n } from "@/hooks/useI18n";
import { AgencyAdvisorMemberRow } from "@/integrations/supabase/rpc-types";

interface TripsFiltersProps {
  search: string;
  onSearchChange: (value: string) => void;
  advisorId: string | null;
  onAdvisorChange: (value: string | null) => void;
  /** Assente per chi vede solo le proprie pratiche. */
  advisors?: AgencyAdvisorMemberRow[];
  isRefreshing: boolean;
  onRefresh: () => void;
  isExporting: boolean;
  /** Righe della vista corrente (tutti i filtri, tutte le pagine). */
  filteredCount: number;
  onExportFiltered: () => void;
  onExportAll: () => void;
}

export function TripsFilters({
  search,
  onSearchChange,
  advisorId,
  onAdvisorChange,
  advisors,
  isRefreshing,
  onRefresh,
  isExporting,
  filteredCount,
  onExportFiltered,
  onExportAll,
}: TripsFiltersProps) {
  const { t } = useI18n();

  const advisorOptions = (advisors ?? []).map((advisor) => ({
    value: advisor.advisor_id,
    label:
      [advisor.first_name, advisor.last_name].filter(Boolean).join(" ") ||
      advisor.email ||
      advisor.advisor_id,
    hint: advisor.email,
  }));

  return (
    <div className="flex flex-wrap items-center gap-2">
      <div className="relative flex-1 min-w-[220px]">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400 pointer-events-none" />
        <Input
          value={search}
          onChange={(e) => onSearchChange(e.target.value)}
          placeholder={t("dashboard.overview.trips.searchPlaceholder")}
          className="pl-9 pr-9"
          aria-label={t("dashboard.overview.trips.searchPlaceholder")}
        />
        {search && (
          <button
            type="button"
            onClick={() => onSearchChange("")}
            className="absolute right-2 top-1/2 -translate-y-1/2 p-1 text-gray-400 hover:text-gray-600"
            aria-label={t("dashboard.overview.clients.clearSearch")}
          >
            <X className="h-4 w-4" />
          </button>
        )}
      </div>

      {advisors !== undefined && (
        <FacetCombobox
          options={advisorOptions}
          value={advisorId}
          onChange={onAdvisorChange}
          placeholder={t("dashboard.overview.clients.advisorFilter")}
          searchPlaceholder={t("dashboard.overview.clients.advisorSearch")}
          clearLabel={t("dashboard.overview.clients.advisorClear")}
          emptyLabel={t("dashboard.overview.clients.advisorEmpty")}
          className="min-w-[180px]"
        />
      )}

      {/* Due export esplici invece di uno solo: "cio' che vedo" e "tutto" sono file
          diversi, e chi scarica deve scegliere sapendo quante righe ci saranno. */}
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button variant="outline" size="sm" disabled={isExporting}>
            {isExporting ? (
              <Loader2 className="h-4 w-4 mr-2 animate-spin" />
            ) : (
              <Download className="h-4 w-4 mr-2" />
            )}
            {t("dashboard.overview.trips.export.button")}
            <ChevronDown className="h-4 w-4 ml-1" />
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end">
          <DropdownMenuItem disabled={filteredCount === 0} onSelect={onExportFiltered}>
            {t("dashboard.overview.trips.export.filtered", { count: filteredCount })}
          </DropdownMenuItem>
          <DropdownMenuItem onSelect={onExportAll}>
            {t("dashboard.overview.trips.export.all")}
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>

      <Button
        variant="outline"
        size="icon"
        onClick={onRefresh}
        disabled={isRefreshing}
        aria-label={t("dashboard.retry")}
      >
        <RefreshCw className={`h-4 w-4 ${isRefreshing ? "animate-spin" : ""}`} />
      </Button>
    </div>
  );
}

export default TripsFilters;
